/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import {
  AutomatedAlertAlgorithmType,
  MissionRoleType,
  OperationType,
  OutOfServiceType,
  SystemTimestampType,
  VehicleType,
  WaypointType,
} from 'src/graphql/generated/graphql.generated';
import { AddressRecord, AddressProps } from 'src/store/generated/address-record.generated';
import {
  IdListItemRecord,
  IdListItemProps,
} from 'src/store/generated/id-list-item-record.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface ActivationRuleProps {
  id: string;
  name: string | null;
  isShared: boolean | null;
  companyId: string | null;
  regionId: string | null;
  baseId: string | null;
  unitId: string | null;
  vehicleId: string | null;
  specialtyTeamId: string | null;
  vehicleTypes: Set<VehicleType> | null;
  medicalFacilityId: string | null;
  locationId: string | null;
  address: AddressProps | null;
  operationTypes: Set<OperationType> | null;
  declineReasons: List<IdListItemProps> | null;
  timestampTypeId: string | null;
  systemTimestampType: SystemTimestampType | null;
  outOfServiceReasonId: string | null;
  paipReasonId: string | null;
  communicationCenters: List<IdListItemProps> | null;
  outOfServiceType: OutOfServiceType | null;
  automatedAlertAlgorithmType: AutomatedAlertAlgorithmType | null;
  ageInMinutes: number | null;
  waypointTypes: Set<WaypointType> | null;
  missionRoleTypes: Set<MissionRoleType> | null;
  companies: List<IdListItemProps> | null;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class ActivationRuleRecord extends Record<ActivationRuleProps>({
  id: null as any,
  name: null as any,
  isShared: null as any,
  companyId: null as any,
  regionId: null as any,
  baseId: null as any,
  unitId: null as any,
  vehicleId: null as any,
  specialtyTeamId: null as any,
  vehicleTypes: null as any,
  medicalFacilityId: null as any,
  locationId: null as any,
  address: null as any,
  operationTypes: null as any,
  declineReasons: null as any,
  timestampTypeId: null as any,
  systemTimestampType: null as any,
  outOfServiceReasonId: null as any,
  paipReasonId: null as any,
  communicationCenters: null as any,
  outOfServiceType: null as any,
  automatedAlertAlgorithmType: null as any,
  ageInMinutes: null as any,
  waypointTypes: null as any,
  missionRoleTypes: null as any,
  companies: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string | null;
    isShared: boolean | null;
    companyId: string | null;
    regionId: string | null;
    baseId: string | null;
    unitId: string | null;
    vehicleId: string | null;
    specialtyTeamId: string | null;
    vehicleTypes: any;
    medicalFacilityId: string | null;
    locationId: string | null;
    address: any;
    operationTypes: any;
    declineReasons: any;
    timestampTypeId: string | null;
    systemTimestampType: SystemTimestampType | null;
    outOfServiceReasonId: string | null;
    paipReasonId: string | null;
    communicationCenters: any;
    outOfServiceType: OutOfServiceType | null;
    automatedAlertAlgorithmType: AutomatedAlertAlgorithmType | null;
    ageInMinutes: number | null;
    waypointTypes: any;
    missionRoleTypes: any;
    companies: any;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): ActivationRuleRecord {
    if (json == null) {
      throw new Error('ActivationRuleRecord.fromJS: parameter cannot be null');
    }
    return new ActivationRuleRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'ActivationRuleJson.id',
      ),
      name: json.name,
      isShared: json.isShared,
      companyId: json.companyId,
      regionId: json.regionId,
      baseId: json.baseId,
      unitId: json.unitId,
      vehicleId: json.vehicleId,
      specialtyTeamId: json.specialtyTeamId,
      vehicleTypes: json.vehicleTypes != null ? Set<VehicleType>(json.vehicleTypes) : null,
      medicalFacilityId: json.medicalFacilityId,
      locationId: json.locationId,
      address: json.address != null ? new AddressRecord(json.address) : null,
      operationTypes: json.operationTypes != null ? Set<OperationType>(json.operationTypes) : null,
      declineReasons:
        json.declineReasons != null
          ? List<IdListItemRecord>(json.declineReasons.map((o: any) => new IdListItemRecord(o)))
          : null,
      timestampTypeId: json.timestampTypeId,
      systemTimestampType: json.systemTimestampType,
      outOfServiceReasonId: json.outOfServiceReasonId,
      paipReasonId: json.paipReasonId,
      communicationCenters:
        json.communicationCenters != null
          ? List<IdListItemRecord>(
              json.communicationCenters.map((o: any) => new IdListItemRecord(o)),
            )
          : null,
      outOfServiceType: json.outOfServiceType,
      automatedAlertAlgorithmType: json.automatedAlertAlgorithmType,
      ageInMinutes: json.ageInMinutes,
      waypointTypes: json.waypointTypes != null ? Set<WaypointType>(json.waypointTypes) : null,
      missionRoleTypes:
        json.missionRoleTypes != null ? Set<MissionRoleType>(json.missionRoleTypes) : null,
      companies:
        json.companies != null
          ? List<IdListItemRecord>(json.companies.map((o: any) => new IdListItemRecord(o)))
          : null,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string | null;
    isShared: boolean | null;
    companyId: string | null;
    regionId: string | null;
    baseId: string | null;
    unitId: string | null;
    vehicleId: string | null;
    specialtyTeamId: string | null;
    vehicleTypes: any;
    medicalFacilityId: string | null;
    locationId: string | null;
    address: any;
    operationTypes: any;
    declineReasons: any;
    timestampTypeId: string | null;
    systemTimestampType: SystemTimestampType | null;
    outOfServiceReasonId: string | null;
    paipReasonId: string | null;
    communicationCenters: any;
    outOfServiceType: OutOfServiceType | null;
    automatedAlertAlgorithmType: AutomatedAlertAlgorithmType | null;
    ageInMinutes: number | null;
    waypointTypes: any;
    missionRoleTypes: any;
    companies: any;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [ActivationRuleRecord, Error[]] {
    if (json == null) {
      throw new Error('ActivationRuleRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new ActivationRuleRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'ActivationRuleJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => json.name,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isShared: tryOrDefault(
        () => json.isShared,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () => json.companyId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      regionId: tryOrDefault(
        () => json.regionId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      baseId: tryOrDefault(
        () => json.baseId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      unitId: tryOrDefault(
        () => json.unitId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      vehicleId: tryOrDefault(
        () => json.vehicleId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      specialtyTeamId: tryOrDefault(
        () => json.specialtyTeamId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      vehicleTypes: tryOrDefault(
        () => (json.vehicleTypes != null ? Set<VehicleType>(json.vehicleTypes) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      medicalFacilityId: tryOrDefault(
        () => json.medicalFacilityId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      locationId: tryOrDefault(
        () => json.locationId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      address: tryOrDefault(
        () => (json.address != null ? new AddressRecord(json.address) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      operationTypes: tryOrDefault(
        () => (json.operationTypes != null ? Set<OperationType>(json.operationTypes) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      declineReasons: tryOrDefault(
        () =>
          json.declineReasons != null
            ? List<IdListItemRecord>(json.declineReasons.map((o: any) => new IdListItemRecord(o)))
            : null,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      timestampTypeId: tryOrDefault(
        () => json.timestampTypeId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      systemTimestampType: tryOrDefault(
        () => json.systemTimestampType,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      outOfServiceReasonId: tryOrDefault(
        () => json.outOfServiceReasonId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      paipReasonId: tryOrDefault(
        () => json.paipReasonId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      communicationCenters: tryOrDefault(
        () =>
          json.communicationCenters != null
            ? List<IdListItemRecord>(
                json.communicationCenters.map((o: any) => new IdListItemRecord(o)),
              )
            : null,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      outOfServiceType: tryOrDefault(
        () => json.outOfServiceType,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      automatedAlertAlgorithmType: tryOrDefault(
        () => json.automatedAlertAlgorithmType,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      ageInMinutes: tryOrDefault(
        () => json.ageInMinutes,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      waypointTypes: tryOrDefault(
        () => (json.waypointTypes != null ? Set<WaypointType>(json.waypointTypes) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      missionRoleTypes: tryOrDefault(
        () => (json.missionRoleTypes != null ? Set<MissionRoleType>(json.missionRoleTypes) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companies: tryOrDefault(
        () =>
          json.companies != null
            ? List<IdListItemRecord>(json.companies.map((o: any) => new IdListItemRecord(o)))
            : null,
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: ActivationRuleProps) {
    super(props);
    if (props == null) {
      throw new Error('ActivationRuleRecord constructor parameter cannot be null');
    }
  }
}

export abstract class ActivationRuleViewModelBase implements ActivationRuleProps {
  protected readonly _props: ActivationRuleProps;
  private _json?: ActivationRuleProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string | null {
    return this._props.name;
  }
  get isShared(): boolean | null {
    return this._props.isShared;
  }
  get companyId(): string | null {
    return this._props.companyId;
  }
  get regionId(): string | null {
    return this._props.regionId;
  }
  get baseId(): string | null {
    return this._props.baseId;
  }
  get unitId(): string | null {
    return this._props.unitId;
  }
  get vehicleId(): string | null {
    return this._props.vehicleId;
  }
  get specialtyTeamId(): string | null {
    return this._props.specialtyTeamId;
  }
  get vehicleTypes(): Set<VehicleType> | null {
    return this._props.vehicleTypes;
  }
  get medicalFacilityId(): string | null {
    return this._props.medicalFacilityId;
  }
  get locationId(): string | null {
    return this._props.locationId;
  }
  get address(): AddressProps | null {
    return this._props.address;
  }
  get operationTypes(): Set<OperationType> | null {
    return this._props.operationTypes;
  }
  get declineReasons(): List<IdListItemProps> | null {
    return this._props.declineReasons;
  }
  get timestampTypeId(): string | null {
    return this._props.timestampTypeId;
  }
  get systemTimestampType(): SystemTimestampType | null {
    return this._props.systemTimestampType;
  }
  get outOfServiceReasonId(): string | null {
    return this._props.outOfServiceReasonId;
  }
  get paipReasonId(): string | null {
    return this._props.paipReasonId;
  }
  get communicationCenters(): List<IdListItemProps> | null {
    return this._props.communicationCenters;
  }
  get outOfServiceType(): OutOfServiceType | null {
    return this._props.outOfServiceType;
  }
  get automatedAlertAlgorithmType(): AutomatedAlertAlgorithmType | null {
    return this._props.automatedAlertAlgorithmType;
  }
  get ageInMinutes(): number | null {
    return this._props.ageInMinutes;
  }
  get waypointTypes(): Set<WaypointType> | null {
    return this._props.waypointTypes;
  }
  get missionRoleTypes(): Set<MissionRoleType> | null {
    return this._props.missionRoleTypes;
  }
  get companies(): List<IdListItemProps> | null {
    return this._props.companies;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isArriveBase() {
    return this._props.systemTimestampType === SystemTimestampType.ArriveBase;
  }
  public get isArriveBedsideReceiving() {
    return this._props.systemTimestampType === SystemTimestampType.ArriveBedsideReceiving;
  }
  public get isArriveDropoff() {
    return this._props.systemTimestampType === SystemTimestampType.ArriveDropoff;
  }
  public get isArriveDropoffLzViaGround() {
    return this._props.systemTimestampType === SystemTimestampType.ArriveDropoffLzViaGround;
  }
  public get isArrivePatient() {
    return this._props.systemTimestampType === SystemTimestampType.ArrivePatient;
  }
  public get isArrivePickup() {
    return this._props.systemTimestampType === SystemTimestampType.ArrivePickup;
  }
  public get isArrivePickupLzViaGround() {
    return this._props.systemTimestampType === SystemTimestampType.ArrivePickupLzViaGround;
  }
  public get isArriveReceivingViaGround() {
    return this._props.systemTimestampType === SystemTimestampType.ArriveReceivingViaGround;
  }
  public get isArriveSendingViaGround() {
    return this._props.systemTimestampType === SystemTimestampType.ArriveSendingViaGround;
  }
  public get isArriveWaypoint() {
    return this._props.systemTimestampType === SystemTimestampType.ArriveWaypoint;
  }
  public get isAssignmentStarted() {
    return this._props.systemTimestampType === SystemTimestampType.AssignmentStarted;
  }
  public get isCardClosed() {
    return this._props.systemTimestampType === SystemTimestampType.CardClosed;
  }
  public get isConfirmedDropoffGround() {
    return this._props.systemTimestampType === SystemTimestampType.ConfirmedDropoffGround;
  }
  public get isConfirmedPickupGround() {
    return this._props.systemTimestampType === SystemTimestampType.ConfirmedPickupGround;
  }
  public get isCrewNotificationReceived() {
    return this._props.systemTimestampType === SystemTimestampType.CrewNotificationReceived;
  }
  public get isCrewNotified() {
    return this._props.systemTimestampType === SystemTimestampType.CrewNotified;
  }
  public get isCustom() {
    return this._props.systemTimestampType === SystemTimestampType.Custom;
  }
  public get isDepartBase() {
    return this._props.systemTimestampType === SystemTimestampType.DepartBase;
  }
  public get isDepartBedsideReceiving() {
    return this._props.systemTimestampType === SystemTimestampType.DepartBedsideReceiving;
  }
  public get isDepartDropoff() {
    return this._props.systemTimestampType === SystemTimestampType.DepartDropoff;
  }
  public get isDepartDropoffLzViaGround() {
    return this._props.systemTimestampType === SystemTimestampType.DepartDropoffLzViaGround;
  }
  public get isDepartPickup() {
    return this._props.systemTimestampType === SystemTimestampType.DepartPickup;
  }
  public get isDepartPickupLzViaGround() {
    return this._props.systemTimestampType === SystemTimestampType.DepartPickupLzViaGround;
  }
  public get isDepartReceivingViaGround() {
    return this._props.systemTimestampType === SystemTimestampType.DepartReceivingViaGround;
  }
  public get isDepartSendingViaGround() {
    return this._props.systemTimestampType === SystemTimestampType.DepartSendingViaGround;
  }
  public get isDepartWaypoint() {
    return this._props.systemTimestampType === SystemTimestampType.DepartWaypoint;
  }
  public get isDepartWithPatient() {
    return this._props.systemTimestampType === SystemTimestampType.DepartWithPatient;
  }
  public get isEmsReport() {
    return this._props.systemTimestampType === SystemTimestampType.EmsReport;
  }
  public get isEnRouteToPatient() {
    return this._props.systemTimestampType === SystemTimestampType.EnRouteToPatient;
  }
  public get isEnRouteToPickup() {
    return this._props.systemTimestampType === SystemTimestampType.EnRouteToPickup;
  }
  public get isEtaProvided() {
    return this._props.systemTimestampType === SystemTimestampType.EtaProvided;
  }
  public get isExternalActionCancellationRequested() {
    return (
      this._props.systemTimestampType === SystemTimestampType.ExternalActionCancellationRequested
    );
  }
  public get isExternalActionRemoveStandby() {
    return this._props.systemTimestampType === SystemTimestampType.ExternalActionRemoveStandby;
  }
  public get isExternalRequestDisregarded() {
    return this._props.systemTimestampType === SystemTimestampType.ExternalRequestDisregarded;
  }
  public get isFboConfirmed() {
    return this._props.systemTimestampType === SystemTimestampType.FboConfirmed;
  }
  public get isFollowUpWeatherCheck() {
    return this._props.systemTimestampType === SystemTimestampType.FollowUpWeatherCheck;
  }
  public get isInitialDeparture() {
    return this._props.systemTimestampType === SystemTimestampType.InitialDeparture;
  }
  public get isManifestConfirmed() {
    return this._props.systemTimestampType === SystemTimestampType.ManifestConfirmed;
  }
  public get isMedicalControlContactAttempted() {
    return this._props.systemTimestampType === SystemTimestampType.MedicalControlContactAttempted;
  }
  public get isOperationCancelled() {
    return this._props.systemTimestampType === SystemTimestampType.OperationCancelled;
  }
  public get isOperationCompleted() {
    return this._props.systemTimestampType === SystemTimestampType.OperationCompleted;
  }
  public get isOverheadDropoff() {
    return this._props.systemTimestampType === SystemTimestampType.OverheadDropoff;
  }
  public get isOverheadPickup() {
    return this._props.systemTimestampType === SystemTimestampType.OverheadPickup;
  }
  public get isPaipEvent() {
    return this._props.systemTimestampType === SystemTimestampType.PaipEvent;
  }
  public get isPatientInfoMessageSent() {
    return this._props.systemTimestampType === SystemTimestampType.PatientInfoMessageSent;
  }
  public get isPlacedOnStandby() {
    return this._props.systemTimestampType === SystemTimestampType.PlacedOnStandby;
  }
  public get isReceivingFacilityRequestAccepted() {
    return this._props.systemTimestampType === SystemTimestampType.ReceivingFacilityRequestAccepted;
  }
  public get isReceivingFacilityRequestCancelled() {
    return (
      this._props.systemTimestampType === SystemTimestampType.ReceivingFacilityRequestCancelled
    );
  }
  public get isReceivingFacilityRequestDenied() {
    return this._props.systemTimestampType === SystemTimestampType.ReceivingFacilityRequestDenied;
  }
  public get isReceivingFacilityRequestDiverted() {
    return this._props.systemTimestampType === SystemTimestampType.ReceivingFacilityRequestDiverted;
  }
  public get isReceivingFacilityRequestReferred() {
    return this._props.systemTimestampType === SystemTimestampType.ReceivingFacilityRequestReferred;
  }
  public get isReferRequest() {
    return this._props.systemTimestampType === SystemTimestampType.ReferRequest;
  }
  public get isRequestCancelled() {
    return this._props.systemTimestampType === SystemTimestampType.RequestCancelled;
  }
  public get isRequestReactivated() {
    return this._props.systemTimestampType === SystemTimestampType.RequestReactivated;
  }
  public get isRequestReceived() {
    return this._props.systemTimestampType === SystemTimestampType.RequestReceived;
  }
  public get isResendToDW() {
    return this._props.systemTimestampType === SystemTimestampType.ResendToDW;
  }
  public get isSentToImageTrend() {
    return this._props.systemTimestampType === SystemTimestampType.SentToImageTrend;
  }
  public get isStandbyRemoved() {
    return this._props.systemTimestampType === SystemTimestampType.StandbyRemoved;
  }
  public get isTransportCheckInitiated() {
    return this._props.systemTimestampType === SystemTimestampType.TransportCheckInitiated;
  }
  public get isUnitAborted() {
    return this._props.systemTimestampType === SystemTimestampType.UnitAborted;
  }
  public get isUnitActivated() {
    return this._props.systemTimestampType === SystemTimestampType.UnitActivated;
  }
  public get isUnitAssigned() {
    return this._props.systemTimestampType === SystemTimestampType.UnitAssigned;
  }
  public get isUnitOffered() {
    return this._props.systemTimestampType === SystemTimestampType.UnitOffered;
  }
  public get isWeatherDeclined() {
    return this._props.systemTimestampType === SystemTimestampType.WeatherDeclined;
  }
  public get isWeatherDelayed() {
    return this._props.systemTimestampType === SystemTimestampType.WeatherDelayed;
  }
  public get isWebOrderAcknowledged() {
    return this._props.systemTimestampType === SystemTimestampType.WebOrderAcknowledged;
  }
  public get isOpportunistic() {
    return this._props.outOfServiceType === OutOfServiceType.Opportunistic;
  }
  public get isRefurbishmentOrRefresh() {
    return this._props.outOfServiceType === OutOfServiceType.RefurbishmentOrRefresh;
  }
  public get isScheduled() {
    return this._props.outOfServiceType === OutOfServiceType.Scheduled;
  }
  public get isUnscheduled() {
    return this._props.outOfServiceType === OutOfServiceType.Unscheduled;
  }
  public get isTimeElapsedFromTimestamp() {
    return (
      this._props.automatedAlertAlgorithmType ===
      AutomatedAlertAlgorithmType.TimeElapsedFromTimestamp
    );
  }

  public isAnySystemTimestampType(...systemTimestampType: SystemTimestampType[]) {
    return (
      this._props.systemTimestampType != null &&
      systemTimestampType.includes(this._props.systemTimestampType)
    );
  }
  public isAnyOutOfServiceType(...outOfServiceType: OutOfServiceType[]) {
    return (
      this._props.outOfServiceType != null &&
      outOfServiceType.includes(this._props.outOfServiceType)
    );
  }
  public isAnyAutomatedAlertAlgorithmType(
    ...automatedAlertAlgorithmType: AutomatedAlertAlgorithmType[]
  ) {
    return (
      this._props.automatedAlertAlgorithmType != null &&
      automatedAlertAlgorithmType.includes(this._props.automatedAlertAlgorithmType)
    );
  }

  constructor(props: ActivationRuleProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        isShared: this._props.isShared,
        companyId: this._props.companyId,
        regionId: this._props.regionId,
        baseId: this._props.baseId,
        unitId: this._props.unitId,
        vehicleId: this._props.vehicleId,
        specialtyTeamId: this._props.specialtyTeamId,
        vehicleTypes: this._props.vehicleTypes,
        medicalFacilityId: this._props.medicalFacilityId,
        locationId: this._props.locationId,
        address: this._props.address,
        operationTypes: this._props.operationTypes,
        declineReasons: this._props.declineReasons,
        timestampTypeId: this._props.timestampTypeId,
        systemTimestampType: this._props.systemTimestampType,
        outOfServiceReasonId: this._props.outOfServiceReasonId,
        paipReasonId: this._props.paipReasonId,
        communicationCenters: this._props.communicationCenters,
        outOfServiceType: this._props.outOfServiceType,
        automatedAlertAlgorithmType: this._props.automatedAlertAlgorithmType,
        ageInMinutes: this._props.ageInMinutes,
        waypointTypes: this._props.waypointTypes,
        missionRoleTypes: this._props.missionRoleTypes,
        companies: this._props.companies,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllActivationRuleEntities = (state: AppState) =>
  state.entityStores.activationRule.entities;

export const selectActivationRuleById = (id: string) => (state: AppState) =>
  state.entityStores.activationRule.entities.get(id);

export const selectActivationRuleMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.activationRule.meta.get(id);

export const selectActivationRuleAndMetadataById = (id: string) =>
  createSelector(
    selectActivationRuleById(id),
    selectActivationRuleMetadataById(id),
    (activationRule, meta) => ({ activationRule, meta }),
  );

export function selectActivationRuleEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllActivationRuleEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, ActivationRuleRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectActivationRuleEntitiesByName(name: string) {
  return createSelector(selectAllActivationRuleEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectActivationRuleEntitiesByIsShared(isShared: boolean) {
  return createSelector(selectAllActivationRuleEntities, (entities) =>
    entities.filter((e) => e.isShared === isShared).valueSeq(),
  );
}

export function selectActivationRuleEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllActivationRuleEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}

export function selectActivationRuleEntitiesByRegionId(regionId: string) {
  return createSelector(selectAllActivationRuleEntities, (entities) =>
    entities.filter((e) => e.regionId === regionId).valueSeq(),
  );
}

export function selectActivationRuleEntitiesByBaseId(baseId: string) {
  return createSelector(selectAllActivationRuleEntities, (entities) =>
    entities.filter((e) => e.baseId === baseId).valueSeq(),
  );
}

export function selectActivationRuleEntitiesByUnitId(unitId: string) {
  return createSelector(selectAllActivationRuleEntities, (entities) =>
    entities.filter((e) => e.unitId === unitId).valueSeq(),
  );
}

export function selectActivationRuleEntitiesByVehicleId(vehicleId: string) {
  return createSelector(selectAllActivationRuleEntities, (entities) =>
    entities.filter((e) => e.vehicleId === vehicleId).valueSeq(),
  );
}

export function selectActivationRuleEntitiesBySpecialtyTeamId(specialtyTeamId: string) {
  return createSelector(selectAllActivationRuleEntities, (entities) =>
    entities.filter((e) => e.specialtyTeamId === specialtyTeamId).valueSeq(),
  );
}

export function selectActivationRuleEntitiesByMedicalFacilityId(medicalFacilityId: string) {
  return createSelector(selectAllActivationRuleEntities, (entities) =>
    entities.filter((e) => e.medicalFacilityId === medicalFacilityId).valueSeq(),
  );
}

export function selectActivationRuleEntitiesByLocationId(locationId: string) {
  return createSelector(selectAllActivationRuleEntities, (entities) =>
    entities.filter((e) => e.locationId === locationId).valueSeq(),
  );
}

export function selectActivationRuleEntitiesByTimestampTypeId(timestampTypeId: string) {
  return createSelector(selectAllActivationRuleEntities, (entities) =>
    entities.filter((e) => e.timestampTypeId === timestampTypeId).valueSeq(),
  );
}

export function selectActivationRuleEntitiesBySystemTimestampType(
  systemTimestampType: SystemTimestampType,
) {
  return createSelector(selectAllActivationRuleEntities, (entities) =>
    entities.filter((e) => e.systemTimestampType === systemTimestampType).valueSeq(),
  );
}

export function selectActivationRuleEntitiesByOutOfServiceReasonId(outOfServiceReasonId: string) {
  return createSelector(selectAllActivationRuleEntities, (entities) =>
    entities.filter((e) => e.outOfServiceReasonId === outOfServiceReasonId).valueSeq(),
  );
}

export function selectActivationRuleEntitiesByPaipReasonId(paipReasonId: string) {
  return createSelector(selectAllActivationRuleEntities, (entities) =>
    entities.filter((e) => e.paipReasonId === paipReasonId).valueSeq(),
  );
}

export function selectActivationRuleEntitiesByOutOfServiceType(outOfServiceType: OutOfServiceType) {
  return createSelector(selectAllActivationRuleEntities, (entities) =>
    entities.filter((e) => e.outOfServiceType === outOfServiceType).valueSeq(),
  );
}

export function selectActivationRuleEntitiesByAutomatedAlertAlgorithmType(
  automatedAlertAlgorithmType: AutomatedAlertAlgorithmType,
) {
  return createSelector(selectAllActivationRuleEntities, (entities) =>
    entities
      .filter((e) => e.automatedAlertAlgorithmType === automatedAlertAlgorithmType)
      .valueSeq(),
  );
}

export function selectActivationRuleEntitiesByAgeInMinutes(ageInMinutes: number) {
  return createSelector(selectAllActivationRuleEntities, (entities) =>
    entities.filter((e) => e.ageInMinutes === ageInMinutes).valueSeq(),
  );
}

//#endregion Selectors
